/**
 * Text-wrap helpers for long unbreakable strings (emails, IDs, paths, URLs).
 *
 * The CSS `word-break: break-word` / `overflow-wrap: break-word` fallback
 * breaks at character boundary when a no-space word doesn't fit. For an
 * email like `marcus.thompson@investorpro.com` in a narrow sidebar card,
 * that produces an ugly line break mid-domain (e.g. `…investorpro.c\nom`).
 *
 * `withSoftBreakPoints()` injects HTML word-break opportunities (`<wbr/>`)
 * after natural break characters — `@`, `.`, `-`, `_`, `/` — so the
 * browser prefers those points before falling back to a mid-character
 * split. For the email above the wrap becomes `marcus.thompson@\ninvestorpro.com`
 * or similar, which reads cleanly.
 *
 * The helper is a no-op for short strings (<24 chars), preserving the
 * original string identity (return type stays `string`) so it doesn't
 * disturb callers that key on the value or pass it to a Tooltip title.
 */

import { Fragment, type ReactNode } from 'react';

/**
 * Strings shorter than this are unlikely to wrap in any reasonable layout,
 * so the helper returns them untouched. Keeps the rendered DOM identical
 * for the common case (names, statuses, short IDs) and only injects
 * `<wbr/>` markup where it could plausibly help.
 */
const MIN_LENGTH_FOR_BREAK_INJECTION = 24;

/**
 * Capturing split — the resulting array alternates [text, delimiter, text, …].
 * Odd indices are the single break-character delimiters.
 */
const BREAK_OPPORTUNITY_SPLIT = /([@._\-/])/;

export function withSoftBreakPoints(value: string): ReactNode {
  if (value.length < MIN_LENGTH_FOR_BREAK_INJECTION) return value;
  const parts = value.split(BREAK_OPPORTUNITY_SPLIT);
  // No break chars present → nothing to inject; let the CSS fallback handle it.
  if (parts.length === 1) return value;
  return parts.map((part, idx) =>
    idx % 2 === 1 ? (
      <Fragment key={idx}>
        {part}
        <wbr />
      </Fragment>
    ) : (
      part
    ),
  );
}
